import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { supabase, uploadFile } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { Upload, X, CheckCircle, AlertCircle } from 'lucide-react'

// ──────────────────────────────────────────────────────────────────────
// Sanitize file name para path de storage. Algunos backends rechazan
// caracteres acentuados o especiales en S3 keys. Normalizamos a ASCII-safe.
// ──────────────────────────────────────────────────────────────────────
function sanitizeForStoragePath(name) {
  if (!name) return 'file'
  return name
    .normalize('NFD')                    // separa acentos (á → a + ´)
    .replace(/[̀-ͯ]/g, '')     // elimina los acentos combinatorios
    .replace(/[^a-zA-Z0-9._-]+/g, '_')   // todo lo no alfanumérico → _
    .replace(/_+/g, '_')                 // múltiples _ consecutivos → uno
    .replace(/^_+|_+$/g, '')             // _ al inicio/final
    .slice(0, 100)                        // límite de longitud razonable
    || 'file'
}

export default function FileUpload({ entityType, entityId, onUploaded }) {
  const { profile, isReadOnly } = useAuth()
  const [files, setFiles]   = useState([])
  const [uploading, setUploading] = useState(false)

  // CEO cliente y otros roles read-only no suben archivos.
  if (isReadOnly) return null

  const onDrop = useCallback(accepted => {
    setFiles(prev => [...prev, ...accepted.map(f => ({ file: f, status: 'pending', error: null }))])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: true })

  function remove(idx) { setFiles(f => f.filter((_, i) => i !== idx)) }

  async function handleUpload() {
    setUploading(true)
    const updated = [...files]
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === 'done') continue
      try {
        const f = updated[i].file
        const safeName = sanitizeForStoragePath(f.name)
        const path = `${entityType}/${entityId}/${Date.now()}_${safeName}`
        console.log('[FileUpload] uploading', { path, originalName: f.name, size: f.size, type: f.type })
        await uploadFile(f, path)
        const { error: insertError } = await supabase.from('files').insert({
          entity_type: entityType,
          entity_id: entityId,
          file_name: f.name,           // nombre ORIGINAL (incluye tildes para mostrar)
          storage_path: path,           // path SANITIZED (sin tildes/espacios)
          file_size: f.size,
          mime_type: f.type,
          uploaded_by: profile.id,
        })
        if (insertError) throw insertError
        updated[i] = { ...updated[i], status: 'done' }
      } catch (err) {
        // Log completo a consola para que F12 muestre el error real
        console.error('[FileUpload] error subiendo archivo:', err)
        const msg = err?.message || err?.error_description || String(err)
        updated[i] = { ...updated[i], status: 'error', error: msg }
      }
      setFiles([...updated])
    }
    setUploading(false)
    onUploaded?.()
    setTimeout(() => setFiles(f => f.filter(fi => fi.status !== 'done')), 2000)
  }

  const pending = files.filter(f => f.status === 'pending').length

  return (
    <div className="space-y-3">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-brand-400 bg-brand-50' : 'border-gray-200 hover:border-brand-300 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <Upload size={24} className={`mx-auto mb-2 ${isDragActive ? 'text-brand-500' : 'text-gray-400'}`} />
        <p className="text-sm text-gray-500">
          {isDragActive ? 'Suelta los archivos aquí' : 'Arrastra archivos aquí o '}
          {!isDragActive && <span className="text-brand-600 font-medium cursor-pointer">haz clic para seleccionar</span>}
        </p>
        <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, imágenes, etc.</p>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <div
              key={i}
              className={`flex flex-col gap-1.5 rounded-lg p-3 ${
                f.status === 'error' ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate">{f.file.name}</p>
                  <p className="text-xs text-gray-400">{(f.file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                {f.status === 'done' && <CheckCircle size={16} className="text-green-500 flex-shrink-0" />}
                {f.status === 'error' && <AlertCircle size={16} className="text-red-500 flex-shrink-0" />}
                {f.status === 'pending' && (
                  <button onClick={() => remove(i)} className="text-gray-400 hover:text-red-500 flex-shrink-0"><X size={14} /></button>
                )}
              </div>
              {/* Mensaje de error VISIBLE (no solo tooltip) — ayuda a debug */}
              {f.status === 'error' && f.error && (
                <p className="text-xs text-red-700 pl-1 break-words">
                  ⚠️ {f.error}
                </p>
              )}
            </div>
          ))}
          {pending > 0 && (
            <button onClick={handleUpload} disabled={uploading} className="btn-primary text-sm w-full">
              {uploading ? 'Subiendo...' : `Subir ${pending} archivo${pending > 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
